import axios from 'axios'
import { i18n } from '@/i18n'
import { resolveExcelHeavyResponse } from '@/shared/utils/excel-heavy-operation'

const publicHttp = axios.create({
  baseURL: '/api/public',
  withCredentials: true,
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
})

publicHttp.interceptors.request.use((config) => {
  const locale = String(i18n.global.locale.value ?? '').trim()
  if (locale) config.headers['X-Locale'] = locale
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    delete config.headers['Content-Type']
  }
  return config
})

export type RecruitmentPortalInfo = {
  name: string
  employee_code?: string | null
  register_url?: string | null
  intake_cutoff_time?: string | null
}

export type RecruitmentPortalJobOrder = {
  id: number
  code: string
  title: string
  client_id?: number | null
  client_name?: string | null
  client_site_id?: number | null
  client_site_name?: string | null
  site_name?: string | null
  position_label?: string | null
  position_title?: string | null
  status?: string | null
  location?: string | null
  starts_at?: string | null
  ends_at?: string | null
  effective_from?: string | null
  effective_to?: string | null
  quota?: number | null
  quota_remaining?: number | null
  quantity_target?: number | null
  allow_over_quota?: boolean | null
  over_quota_limit?: number | null
  receive_cap?: number | null
  receive_remaining?: number | null
  demand_type?: string | null
  period_quantity?: number | null
  onboard_date?: string | null
  demand_by_date?: string | null
  description?: string | null
  working_hours?: string | null
  work_shift?: string | null
  education_required?: string | null
  experience_required?: string | null
  requires_captcha?: boolean | null
  require_province_ward?: boolean | null
  age_rule?: { min?: number | null; max?: number | null } | null
  gender_rule?: Record<string, unknown> | null
  salary_policy?: Record<string, unknown> | null
  reward_policy?: Record<string, unknown> | null
  reward_programs_by_policy_id?: Record<number, unknown[]> | null
  settlement_policy?: Record<string, unknown> | null
  settlement_notes?: string | null
  client_pricing?: Record<string, unknown> | null
  visibility?: {
    view_salary?: boolean
    view_requirements?: boolean
    view_client_pricing?: boolean
    can_view_supplier_reconcile?: boolean
    can_submit_candidate?: boolean
  } | null
  benefits?: Record<string, unknown> | null
  benefits_text?: string | null
  work_profile?: Record<string, unknown> | null
  compensations?: unknown[]
  section_contents?: unknown[]
  worker_rewards?: unknown[]
  commissions?: unknown[]
  can_submit?: boolean
  can_submit_candidate?: boolean
  can_submit_now?: boolean
}

export type RecruitmentPortalRegistrationPayload = {
  job_order_id: number
  interview_date: string
  interview_time?: string
  full_name: string
  phone: string
  identity_number: string
  date_of_birth: string
  gender: string
  address: string
  province_code: string
  province?: string
  ward_code: string
  ward?: string
  place_of_origin?: string
  nationality?: string
  issue_date: string
  issue_place: string
  expiry_date?: string
  recognition_method?: string
  recognition_confidence?: number | null
  recognition_payload?: Record<string, unknown> | null
  front_image_path?: string
  back_image_path?: string
  notes?: string
  captcha_id?: string
  captcha_answer?: string
  /** Approach source («Nguồn tiếp cận») — guest QR + staff portal. */
  platform_id?: number | null
  /** Optional when referrer already exists as a worker. */
  referrer_worker_id?: number | null
  /** External / free-text referrer (GT sources). */
  referrer_name?: string | null
  referrer_phone?: string | null
  referrer_identity_number?: string | null
  referrer_address?: string | null
  referrer_bank_account?: string | null
  /** Uniapp field register «mở form trên máy này». */
  field_assist_token?: string | null
  /** Care lead (raw_leads.id) opened from personal workbench Register. */
  lead_id?: number | null
  /** Open lead_assignments.id for the same care row. */
  assignment_id?: number | null
  /** How this portal submit should land on the workbench. */
  intake_mode?: 'lead_care' | 'walk_in' | 'staff_proxy' | null
}

export type RecruitmentPortalApproachPlatform = {
  id: number
  name: string
  code?: string | null
  platform_code?: string | null
  platform_name?: string | null
}

export type RecruitmentPortalRegistrationResult = {
  application_id: number
  worker_id: number
  interview_id: number | null
  partner_push_failed?: boolean
  partner_push_pending?: boolean
  partner_push_succeeded?: boolean
  partner_send_mode?: string | null
  rejection_reason?: string | null
  display_status?: string | null
  submit_warnings?: string[] | null
}

/** Guest history / detail payload (application_id as id). */
export type RecruitmentPortalFactoryChangeEntry = {
  mode?: 'moved' | 'new_cycle' | string | null
  from_application_id?: number | null
  to_application_id?: number | null
  from_status?: string | null
  from_interview_status?: string | null
  from_interview_result?: string | null
  from_interview_id?: number | null
  preserves_source_dossier?: boolean | null
  from_job_order_id?: number | null
  from_job_order_code?: string | null
  from_client_id?: number | null
  from_client_name?: string | null
  from_factory?: string | null
  to_job_order_id?: number | null
  to_job_order_code?: string | null
  to_client_id?: number | null
  to_client_name?: string | null
  to_factory?: string | null
  changed_at?: string | null
  changed_by_user_id?: number | null
  changed_by_name?: string | null
}

export type RecruitmentPortalGuestRegistration = {
  id: number
  application_id?: number
  full_name?: string
  phone?: string | null
  phone_masked?: string | null
  identity_number?: string | null
  identity_number_display?: string | null
  date_of_birth?: string | null
  gender?: string | null
  address?: string | null
  province_code?: string | null
  province?: string | null
  ward_code?: string | null
  ward?: string | null
  place_of_origin?: string | null
  nationality?: string | null
  issue_date?: string | null
  issue_place?: string | null
  expiry_date?: string | null
  interview_date?: string | null
  interview_time?: string | null
  /** Approach source (nguồn tiếp cận) — required on staff amend resubmit. */
  platform_id?: number | null
  referrer_name?: string | null
  referrer_phone?: string | null
  referrer_identity_number?: string | null
  referrer_address?: string | null
  referrer_bank_account?: string | null
  notes?: string | null
  front_image_path?: string | null
  back_image_path?: string | null
  front_image_url?: string | null
  back_image_url?: string | null
  status?: string
  display_status?: string | null
  display_status_tone?: string | null
  rejection_reason?: string | null
  partner_push_failed?: boolean
  partner_push_pending?: boolean
  partner_push_succeeded?: boolean
  partner_send_mode?: string | null
  submitted_at?: string | null
  job_order_id?: number | null
  client_id?: number | null
  client_name?: string | null
  job_order?: { id: number; code: string; title: string } | null
  client?: { id: number; name: string } | null
  can_change_factory?: boolean
  require_province_ward?: boolean | null
  /** Last factory-change mode: moved (same row) or new_cycle (spawn). */
  factory_change_last_mode?: 'moved' | 'new_cycle' | string | null
  /** This row is the new cycle spawned from that application id. */
  spawned_from_application_id?: number | null
  /** This row is the concluded source that spawned that application id. */
  spawned_to_application_id?: number | null
  factory_change_history?: RecruitmentPortalFactoryChangeEntry[] | null
}

export type PublicQrImageDecodeResult = {
  payload?: string | null
}

function unwrapData<T>(res: { data?: { data?: T } | T }): T {
  const body = res.data as { data?: T } | T
  if (body && typeof body === 'object' && 'data' in body) {
    return (body as { data: T }).data
  }
  return body as T
}

const noStoreGet = {
  headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
} as const

export const recruitmentPortalApi = {
  async resolve(token: string): Promise<RecruitmentPortalInfo> {
    const res = await publicHttp.get(`/recruitment-portal/${encodeURIComponent(token)}`, noStoreGet)
    return unwrapData<RecruitmentPortalInfo>(res)
  },

  async jobOrders(token: string): Promise<RecruitmentPortalJobOrder[]> {
    const res = await publicHttp.get(`/recruitment-portal/${encodeURIComponent(token)}/job-orders`, noStoreGet)
    const data = unwrapData<RecruitmentPortalJobOrder[] | { data: RecruitmentPortalJobOrder[] }>(res)
    if (Array.isArray(data)) return data
    if (data && typeof data === 'object' && Array.isArray(data.data)) return data.data
    return []
  },

  async parseQr(token: string, payload: string) {
    const res = await publicHttp.post(`/recruitment-portal/${encodeURIComponent(token)}/parse-qr`, { payload })
    return unwrapData<Record<string, unknown>>(res)
  },

  async decodeQrImage(token: string, image: File, signal?: AbortSignal) {
    const form = new FormData()
    form.append('image', image)
    const res = await publicHttp.post(`/recruitment-portal/${encodeURIComponent(token)}/decode-qr-image`, form, {
      signal,
      timeout: 180_000,
      validateStatus: (status) => (status >= 200 && status < 300) || status === 202,
    })
    return resolveExcelHeavyResponse<PublicQrImageDecodeResult>(res.data, { timeoutMs: 180_000 })
  },

  async classifySide(token: string, image: File, expectedSide: 'front' | 'back') {
    const form = new FormData()
    form.append('image', image)
    form.append('expected_side', expectedSide)
    const res = await publicHttp.post(`/recruitment-portal/${encodeURIComponent(token)}/classify-side`, form, {
      timeout: 180_000,
      validateStatus: (status) => (status >= 200 && status < 300) || status === 202,
    })
    return resolveExcelHeavyResponse<{
      side: string
      confidence: number
      upload_verdict?: 'ok' | 'wrong_side' | 'not_cccd' | 'unknown'
      provider?: string
    }>(res.data, { timeoutMs: 180_000 })
  },

  async recognize(token: string, front: File, back?: File) {
    const form = new FormData()
    form.append('front', front)
    if (back) form.append('back', back)
    const res = await publicHttp.post(`/recruitment-portal/${encodeURIComponent(token)}/recognize`, form, {
      timeout: 180_000,
      validateStatus: (status) => (status >= 200 && status < 300) || status === 202,
    })
    return resolveExcelHeavyResponse<Record<string, unknown>>(res.data, { timeoutMs: 180_000 })
  },

  async fetchCaptcha(token: string, jobOrderId: number) {
    const res = await publicHttp.get(`/recruitment-portal/${encodeURIComponent(token)}/captcha`, {
      params: { job_order_id: jobOrderId },
    })
    return unwrapData<{
      captcha_id: string | null
      captcha_svg: string | null
      requires_captcha?: boolean
    }>(res)
  },

  async listPlatforms(token: string): Promise<RecruitmentPortalApproachPlatform[]> {
    const res = await publicHttp.get(
      `/recruitment-portal/${encodeURIComponent(token)}/platforms`,
      noStoreGet,
    )
    const data = unwrapData<RecruitmentPortalApproachPlatform[] | { data: RecruitmentPortalApproachPlatform[] }>(res)
    if (Array.isArray(data)) return data
    if (data && typeof data === 'object' && Array.isArray(data.data)) return data.data
    return []
  },

  async submit(token: string, payload: RecruitmentPortalRegistrationPayload) {
    try {
      const res = await publicHttp.post(
        `/recruitment-portal/${encodeURIComponent(token)}/registrations`,
        payload,
        {
          timeout: 200_000,
          validateStatus: (status) => (status >= 200 && status < 300) || status === 202,
        },
      )
      return await resolveExcelHeavyResponse<RecruitmentPortalRegistrationResult>(res.data, {
        timeoutMs: 200_000,
      })
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const body = error.response?.data as { data?: RecruitmentPortalRegistrationResult } | undefined
        if (error.response?.status === 422 && body?.data && typeof body.data === 'object') {
          const failed = body.data
          if (Number(failed.application_id ?? 0) > 0) {
            return failed
          }
        }
      }
      throw error
    }
  },

  async listRegistrations(token: string, ids: number[] = []): Promise<RecruitmentPortalGuestRegistration[]> {
    const knownIds = ids.filter((id) => Number.isFinite(id) && id > 0).slice(0, 200)
    if (knownIds.length === 0) return []
    const res = await publicHttp.get(
      `/recruitment-portal/${encodeURIComponent(token)}/registrations`,
      { ...noStoreGet, params: { ids: knownIds } },
    )
    const data = unwrapData<RecruitmentPortalGuestRegistration[] | { data: RecruitmentPortalGuestRegistration[] }>(res)
    if (Array.isArray(data)) return data
    if (data && typeof data === 'object' && Array.isArray(data.data)) return data.data
    return []
  },

  async getRegistration(token: string, id: number): Promise<RecruitmentPortalGuestRegistration> {
    const res = await publicHttp.get(
      `/recruitment-portal/${encodeURIComponent(token)}/registrations/${id}`,
      noStoreGet,
    )
    return unwrapData<RecruitmentPortalGuestRegistration>(res)
  },

  async changeFactory(
    token: string,
    id: number,
    payload: {
      job_order_id: number
      captcha_id?: string
      captcha_answer?: string
      province_code?: string
      province?: string
      ward_code?: string
      ward?: string
    },
  ): Promise<RecruitmentPortalGuestRegistration> {
    const res = await publicHttp.patch(
      `/recruitment-portal/${encodeURIComponent(token)}/registrations/${id}/factory`,
      payload,
      {
        timeout: 200_000,
        validateStatus: (status) => (status >= 200 && status < 300) || status === 202,
      },
    )
    return resolveExcelHeavyResponse<RecruitmentPortalGuestRegistration>(res.data, {
      timeoutMs: 200_000,
    })
  },

  async exportRegistrations(token: string, ids: number[]): Promise<Blob> {
    const knownIds = ids.filter((id) => Number.isFinite(id) && id > 0).slice(0, 50)
    const res = await publicHttp.get(
      `/recruitment-portal/${encodeURIComponent(token)}/registrations/export`,
      {
        params: { ids: knownIds },
        responseType: 'blob',
      },
    )
    return res.data as Blob
  },

  async submitToLuxshare(payload: {
    job_order_id: number
    name: string
    gender: string
    birthday: string
    address: string
    iDCode: string
    oldIDCode?: string
    nation: string
    issuanceAuthority: string
    efectiveStartDate: string
    efectiveEndDate: string
    dataFrom: string
    verCode: string
    registedBy: string
    registedByName: string
  }) {
    const res = await publicHttp.post('/recruitment-portal/luxshare/regist-vn', payload, {
      timeout: 200_000,
      validateStatus: (status) => (status >= 200 && status < 300) || status === 202,
    })
    const data = await resolveExcelHeavyResponse<Record<string, unknown>>(res.data, {
      timeoutMs: 200_000,
    })
    return { data }
  },
}
